import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { checkFigure, formatRate, isRate, noteText } from './fxFormat';

import { formatDateLong, toDateString, toLocalDate } from 'Helpers/dateFormat';

import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import TextButton from 'CommonComponents/TextButton';

export function CheckFigure({ currency, anchor, rate }) {
    const figure = checkFigure(currency, anchor, rate);

    if (!figure) {
        return null;
    }

    return (
        <span className="tw-text-xs tw-text-charcoal-75 tw-whitespace-nowrap">
            {figure.from} = <span className="tw-font-semibold tw-text-charcoal">{figure.to}</span>
        </span>
    );
}

/**
 * A rate input: right aligned, flagged while blank. It shows four places
 * until focused, then the full rate for editing; the value it holds and sends
 * is always the full text.
 */
export function RateInput({ value, onChange, onCommit, onFocus, onBlur, placeholder, invalid = false, className, ...props }) {
    const [focused, setFocused] = useState(false);
    const shown = focused || !isRate(value) ? value : formatRate(value);

    return (
        <input
            type="text"
            inputMode="decimal"
            className={classNames(className || 'text-input !tw-w-28', 'tw-text-end tw-tabular-nums', {
                '!tw-border-amber-400 !tw-bg-amber-50': value === '' && !invalid,
                '!tw-border-red': invalid,
            })}
            value={shown}
            placeholder={placeholder}
            onChange={(event) => onChange(event.target.value)}
            onFocus={(event) => {
                setFocused(true);
                onFocus?.(event);
            }}
            onBlur={(event) => {
                setFocused(false);
                onBlur?.(event);
                onCommit?.();
            }}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    onCommit?.();
                }
            }}
            {...props}
        />
    );
}

/** An as-at date picker that opens on click, not on tabbing through the row. */
export function AsAtPicker({ value, onChange }) {
    return (
        <DatePickerField
            name="as_at"
            className="text-input !tw-w-32"
            value={toLocalDate(value) || null}
            dateFormat="d MMM yyyy"
            preventOpenOnFocus
            onChange={(_, date) => onChange(toDateString(date))}
        />
    );
}

/** The flag a rebased row carries until its rate is saved or marked reviewed. */
export function ReviewNote({ noteKey, noteParams, onReview }) {
    return (
        <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-amber-700">
            {$t('{{note}}: review', { note: noteText(noteKey, noteParams) || $t('Rebased') })}
            <TextButton variant="primary" className="!tw-normal-case" onClick={onReview}>
                {$t('Mark reviewed')}
            </TextButton>
        </div>
    );
}

function RateRow({ row, anchor, onSave, onRemove, onReview }) {
    // Inputs hold the rate as stored, to six places, so leaving one untouched
    // saves nothing and the check figure reads the stored rate.
    const [rate, setRate] = useState(row.rate ?? '');
    const [source, setSource] = useState(row.source || '');

    // A save answers with the whole page; the row follows what was stored.
    useEffect(() => setRate(row.rate ?? ''), [row.rate]);
    useEffect(() => setSource(row.source || ''), [row.source]);

    // The rate on its way to the server: Enter then leaving the field commits
    // twice before the first save answers, and only one is sent.
    const sending = useRef(null);

    const invalid = rate !== '' && !isRate(rate);

    const commitRate = () => {
        if (invalid || rate === (row.rate ?? '') || rate === sending.current) {
            return;
        }

        sending.current = rate;
        Promise.resolve(onSave(row, { rate: rate === '' ? null : rate.trim() })).finally(() => {
            sending.current = null;
        });
    };

    const commitSource = () => {
        if (source !== (row.source || '')) {
            onSave(row, { source: source || null });
        }
    };

    return (
        <tr>
            <td className="tw-text-start">
                <Badge variant="neutral" size="md">
                    {row.currency}
                </Badge>
                {row.needs_review ? (
                    <ReviewNote noteKey={row.note_key} noteParams={row.note_params} onReview={() => onReview(row)} />
                ) : (
                    row.note_key && <div className="tw-mt-1 tw-text-xs tw-text-charcoal-50">{noteText(row.note_key, row.note_params)}</div>
                )}
            </td>
            <td className="inbud-numeric-cell">
                <RateInput
                    value={rate}
                    onChange={setRate}
                    onCommit={commitRate}
                    placeholder={$t('enter rate')}
                    invalid={invalid}
                    aria-label={$t('Rate for {{currency}}', { currency: row.currency })}
                />
                {invalid && <div className="tw-mt-1 tw-text-xs tw-text-red">{$t('Enter a rate above zero, with up to 6 decimal places')}</div>}
            </td>
            <td className="tw-text-start">
                {isRate(rate) ? (
                    <CheckFigure currency={row.currency} anchor={anchor} rate={rate} />
                ) : (
                    <Badge variant="warning" size="md">
                        {$t('No rate: {{currency}} projects cannot be consolidated', { currency: row.currency })}
                    </Badge>
                )}
            </td>
            <td className="tw-text-start">
                <AsAtPicker value={row.as_at} onChange={(asAt) => onSave(row, { as_at: asAt })} />
            </td>
            <td className="tw-text-start">
                <input
                    type="text"
                    className="text-input tw-min-w-[140px]"
                    value={source}
                    placeholder={$t('source or note')}
                    aria-label={$t('Source or note for {{currency}}', { currency: row.currency })}
                    onChange={(event) => setSource(event.target.value)}
                    onBlur={commitSource}
                />
            </td>
            <td className="tw-text-start tw-text-xs tw-text-charcoal-75">
                {row.updated_by && (
                    <>
                        <div>{row.updated_by}</div>
                        <div className="tw-text-charcoal-50">{formatDateLong(row.updated_at)}</div>
                    </>
                )}
            </td>
            <td className="tw-text-end">
                {/* The title sits on a wrapper too: a disabled button shows no tooltip in some browsers. */}
                <span title={row.in_use ? $t('In use by a project') : undefined}>
                    <Button
                        variant="secondary"
                        small
                        disabled={row.in_use}
                        title={row.in_use ? $t('In use by a project') : undefined}
                        aria-label={row.in_use ? $t('Remove {{currency}}: in use by a project', { currency: row.currency }) : undefined}
                        onClick={() => onRemove(row)}
                    >
                        {$t('Remove')}
                    </Button>
                </span>
            </td>
        </tr>
    );
}

function AddCurrencyRow({ currencies, anchor, onAdd, onCancel }) {
    const [currencyId, setCurrencyId] = useState(currencies[0]?.id ?? '');
    const [rate, setRate] = useState('');
    const [asAt, setAsAt] = useState(null);
    const [source, setSource] = useState('');

    const currency = currencies.find((option) => String(option.id) === String(currencyId));
    const invalid = rate !== '' && !isRate(rate);

    return (
        <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <select
                className="tw-border-2 tw-border-grey tw-rounded-lg tw-text-sm tw-text-charcoal tw-px-3 tw-py-1.5 tw-bg-white"
                value={currencyId}
                aria-label={$t('Currency')}
                onChange={(event) => setCurrencyId(event.target.value)}
            >
                {currencies.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.code}
                    </option>
                ))}
            </select>
            <RateInput value={rate} onChange={setRate} placeholder={$t('rate')} invalid={invalid} aria-label={$t('Rate')} />
            <AsAtPicker value={asAt} onChange={setAsAt} />
            <input
                type="text"
                className="text-input !tw-w-52"
                value={source}
                placeholder={$t('source or note')}
                aria-label={$t('Source or note')}
                onChange={(event) => setSource(event.target.value)}
            />
            {currency && <CheckFigure currency={currency.code} anchor={anchor} rate={rate} />}
            <Button
                variant="primary"
                small
                disabled={!currency || invalid}
                onClick={() => onAdd({ currency_id: currency.id, rate: rate === '' ? null : rate.trim(), as_at: asAt, source: source || null })}
            >
                {$t('Add')}
            </Button>
            <Button variant="secondary" small onClick={onCancel}>
                {$t('Cancel')}
            </Button>
        </div>
    );
}

/**
 * The common rates: one row per currency, "1 anchor = rate", each field saved
 * as it is left. A currency a project uses cannot be removed.
 */
export default function FxRatesTable({ rates, currencies, anchor, onSave, onAdd, onRemove, onReview }) {
    const [adding, setAdding] = useState(false);

    const inTable = new Set(rates.map((row) => row.currency_id));
    const addable = currencies.filter((currency) => currency.code !== anchor && !inTable.has(currency.id));

    return (
        <div className="table__card">
            <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4 tw-mb-4">
                <div>
                    <h2 className="tw-text-blue tw-text-xl tw-font-semibold tw-mb-1">{$t('Common rates')}</h2>
                    <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                        {$t('Enter each rate as 1 {{anchor}} = X. Projects in {{anchor}} use 1.0000 and need no rate.', { anchor })}
                    </p>
                </div>
                <Button variant="secondary" disabled={addable.length === 0} onClick={() => setAdding((open) => !open)}>
                    {$t('Add currency')}
                </Button>
            </div>

            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th className="!tw-text-start">{$t('Currency')}</th>
                            <th className="!tw-text-end">{$t('1 {{anchor}} =', { anchor })}</th>
                            <th className="!tw-text-start">{$t('Check')}</th>
                            <th className="!tw-text-start">{$t('As at')}</th>
                            <th className="!tw-text-start">{$t('Source or note')}</th>
                            <th className="!tw-text-start">{$t('Last updated')}</th>
                            <th>
                                <span className="tw-sr-only">{$t('Actions')}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rates.length === 0 && (
                            <tr>
                                <td colSpan={7} className="tw-text-center tw-text-charcoal-75">
                                    {$t('No currencies yet. Every project is in the anchor currency.')}
                                </td>
                            </tr>
                        )}
                        {rates.map((row) => (
                            <RateRow key={row.id} row={row} anchor={anchor} onSave={onSave} onRemove={onRemove} onReview={onReview} />
                        ))}
                    </tbody>
                </table>
            </div>

            {adding && (
                <AddCurrencyRow
                    currencies={addable}
                    anchor={anchor}
                    onAdd={(payload) => onAdd(payload).then((ok) => ok && setAdding(false))}
                    onCancel={() => setAdding(false)}
                />
            )}
        </div>
    );
}
